import React, { useEffect, useState } from "react";
import { drawDetailsProps } from "../components/apis/getDrawDetail";
import useWindowSize from "../components/utils/useWindowSize";
import BingoCards from "../components/BingoCards";
import UserProfile from "../components/UserProfile";
import { UserProps } from "../components/apis/user";
import { remainingPatternsProps } from "../components/BingoPage";
import { gridRowsProps, ticketsProps } from "../components/apis/getTickets";
import Timer from "../components/Timer";
import { generateBingoLotteryTicket } from "../components/utils/generateTickets";
import {
  animateBingoBall,
  setBingoAnimationVisiblity,
} from "../components/pixi-app/bridge";
import DemoBingoTickets, { WinningResult } from "./DemoBingoTickets";
import { closeModalData } from "./DemoPlayApp";
import { unlockAudioContext } from "../hooks/unlockAudioContext";
import useCreateSound from "../hooks/useCreateSound";
import { BALL_ANIMATION_TIMER } from "../envVariables";
interface DemoGamePageProps {
  drawDetails: drawDetailsProps | undefined;
  userWallet?: UserProps;
  ticketData: ticketsProps | undefined;
  selectTickets: number;
  handleTicketData: (data: ticketsProps) => void;
  handleCloseModal: (data: closeModalData) => void;
  handleShowWinnersModal: (status: boolean) => void;
  handleWinnersData: (data: { [ticketId: string]: WinningResult[] }) => void;
  winningTickets: { [ticketId: string]: WinningResult[] };
  showWinners: boolean;
  closeModal: closeModalData;
  amount: number;
  handleAmount: (data: number) => void;
  handleReducedCount: (data: number) => void;
}

export default function DemoGamePage({
  drawDetails,
  closeModal,
  handleReducedCount,
  handleAmount,
  showWinners,
  amount,
  handleTicketData,
  handleWinnersData,
  winningTickets,
  handleCloseModal,
  handleShowWinnersModal,
  selectTickets,
  userWallet,
  ticketData,
}: DemoGamePageProps) {
  const { width, height } = useWindowSize();
  const [calledNumbers, setCalledNumbers] = useState<number[]>([]);
  const [autoClaim, setAutoClaim] = useState<boolean>(false);
  const [remainingPatterns, setRemainingPatterns] = useState<
    remainingPatternsProps | undefined
  >();
  const [showBingoAnimation, setShowBingoAnimation] = useState<boolean>(false);
  let count;
  const getGameName = drawDetails ? drawDetails.draw.game.name : "BINGO";
  const text = getGameName.charAt(0).toUpperCase() + getGameName.slice(1);
  const maxNumberRange = drawDetails
    ? drawDetails.draw.game.maxNumberRange
    : 75;
  const isMobile = width <= 1024;
  const hasWinners = Object.keys(winningTickets || {}).length > 0;
  const BallCount = calledNumbers && calledNumbers.length === maxNumberRange;
  const { playSound } = useCreateSound();

  const toggleAutoClaim = (prev: boolean) => {
    setAutoClaim(!prev);
  };
  useEffect(() => {
    if (showBingoAnimation) {
      setTimeout(() => {
        setShowBingoAnimation(false);
      }, 1500);
    }
  }, [showBingoAnimation]);

  const handleClaimAnimation = (status: boolean) => {
    setShowBingoAnimation(status);
  };
  const generateUniqueId = () => {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };
  useEffect(() => {
    const getGridSize = drawDetails ? drawDetails.draw.game.gridSize : "5x5";

    const newTickets: gridRowsProps[] = Array.from(
      { length: selectTickets },
      () => {
        const gridItems = generateBingoLotteryTicket(
          getGridSize?.toString(),
          maxNumberRange
        );
        return {
          id: generateUniqueId(),
          gameId: drawDetails?.draw.game.id || "",
          drawId: drawDetails?.draw.id || "",
          gridItems,
          game: {
            gridSize: getGridSize?.toString() || "5x5",
          },
        };
      }
    );

    handleTicketData({
      count: selectTickets,
      rows: newTickets,
    });
  }, []);
  const ballTimer = Number(BALL_ANIMATION_TIMER) || 1000;

  useEffect(() => {
    if (!maxNumberRange) return;

    // Helper function to generate and set the next number
    const generateNextNumber = () => {
      setCalledNumbers((prev) => {
        if (prev.length >= maxNumberRange) return prev;

        let nextNumber: number;
        do {
          nextNumber = Math.floor(Math.random() * maxNumberRange) + 1;
        } while (prev.includes(nextNumber));

        if (!showWinners && !closeModal.status) {
          playSound(nextNumber);
        }

        return [...prev, nextNumber];
      });
    };

    // Call once immediately
    generateNextNumber();

    // Then set up the interval
    const interval = setInterval(generateNextNumber, ballTimer);

    return () => clearInterval(interval);
  }, [maxNumberRange, showWinners, closeModal, ballTimer]);

  useEffect(() => {
    if (!showWinners && !closeModal.status) {
      setBingoAnimationVisiblity();
      const latestNumber = calledNumbers[calledNumbers.length - 1];
      calledNumbers?.length > 0 &&
        animateBingoBall(
          calledNumbers,
          latestNumber,
          maxNumberRange,
          (count = calledNumbers.length)
        );
    }
  }, [calledNumbers]);

  useEffect(() => {
    if (drawDetails?.draw?.prizes) {
      const patterns = drawDetails.draw.prizes;
      let remainingPatternData: { [key: string]: number } = {};
      let totalRemaining = 0;
      let totalRemainingPrice = 0;

      Object.keys(patterns).forEach((patternKey) => {
        const pattern = patterns[patternKey];
        const remainingCount = pattern.winCount;

        remainingPatternData[patternKey] = remainingCount;

        totalRemaining += remainingCount;
        totalRemainingPrice += remainingCount * pattern.price;
      });

      setRemainingPatterns({
        patterns: remainingPatternData,
        totalRemaining,
        totalRemainingPrice,
      });
    }
  }, [drawDetails]);

  const handleRemainingCount = (type: string) => {
    const prizeValue = drawDetails?.draw?.prizes?.[type]?.price || 0;
    setRemainingPatterns((prev) => {
      if (!prev || prev.patterns[type] <= 0) return prev;
      const updatedPatterns = { ...prev.patterns };
      updatedPatterns[type] = updatedPatterns[type] - 1;

      return {
        patterns: updatedPatterns,
        totalRemaining: prev.totalRemaining - 1,
        totalRemainingPrice: prev.totalRemainingPrice - prizeValue,
      };
    });
    // handleAmount(amount+prizeValue)
  };

  useEffect(() => {
    if (
      (remainingPatterns && remainingPatterns.totalRemaining === 0) ||
      BallCount
    ) {
      handleShowWinnersModal(true);
      // if (hasWinners) {
      //   handleShowWinnersModal(true);
      // } else {
      //   handleCloseModal({ status: true, test: "The game has ended." })
      // }
    }
  }, [
    remainingPatterns,
    showWinners,
    handleShowWinnersModal,
    BallCount,
    handleCloseModal,
  ]);
  useEffect(() => {
    const handleUnlock = () => {
      unlockAudioContext();
      window.removeEventListener("scroll", handleUnlock);
      window.removeEventListener("click", handleUnlock);
      window.removeEventListener("touchstart", handleUnlock);
    };

    window.addEventListener("scroll", handleUnlock);
    window.addEventListener("click", handleUnlock);
    window.addEventListener("touchstart", handleUnlock);

    return () => {
      window.removeEventListener("scroll", handleUnlock);
      window.removeEventListener("click", handleUnlock);
      window.removeEventListener("touchstart", handleUnlock);
    };
  }, []);

  useEffect(() => {
    if (!remainingPatterns || !drawDetails?.draw?.prizes) return;

    const availablePatterns = Object.keys(remainingPatterns.patterns).filter(
      (pattern) => remainingPatterns.patterns[pattern] > 0
    );
    const halfwayReached = calledNumbers.length >= maxNumberRange / 2;
    if (!halfwayReached) return;
    if (availablePatterns.length === 0) return;

    let timeoutId: NodeJS.Timeout;

    const scheduleNext = () => {
      if (availablePatterns.length === 0) return;

      // const randomDelay = Math.floor(Math.random() * 5000);
      const randomDelay =
        Math.floor(Math.random() * (ballTimer + 2000 - 1000 + 1)) + 1000;
      timeoutId = setTimeout(() => {
        const randomIndex = Math.floor(
          Math.random() * availablePatterns.length
        );
        const selectedPattern = availablePatterns[randomIndex];

        handleRemainingCount(selectedPattern);
        handleReducedCount(1);
        if (remainingPatterns.patterns[selectedPattern] <= 1) {
          const indexToRemove = availablePatterns.indexOf(selectedPattern);
          if (indexToRemove !== -1) {
            availablePatterns.splice(indexToRemove, 1);
          }
        }

        if (availablePatterns.length > 0) {
          scheduleNext();
        }
      }, randomDelay);
    };

    scheduleNext();

    return () => clearTimeout(timeoutId);
  }, [remainingPatterns, drawDetails, calledNumbers, maxNumberRange]);

  const unerAmount = {
    amount: amount,
  };
  return (
    <>

      <div
        className={`w-full relative flex flex-col min-h-[550px]    h-screen pb-2 `}


      >
        <div className="flex justify-center items-center md:py-4 py-1 xl:py-4 md:px-4 px-2">
          <h1
            className={` md:text-2xl text-base  max-w-[600px] font-bold text-[#40CDF9] z-40 drop-shadow-[0_0_10px_rgba(0,200,255,0.8)]`}
          >
            {width <= 620 && text.length >= 15
              ? text.slice(0, 15) + "..."
              : width <= 950 && text.length > 15
                ? text.slice(0, 15) + "..."
                : text}
          </h1>
        </div>

        <div className="flex md:items-center gap-4 md:gap-10  z-30 justify-between  w-full md:px-4 px-2 ">
          <div
            className={`lg:flex-shrink-0 lg:min-w-[300px]`}
          >
            {false && (
              <div className={`flex lg:pt-8 md:pt-8 pt-3 h-full w-full`}>
                <Timer drawTime={new Date()} />
              </div>
            )}

          </div>
          <div className=" xl:flex hidden flex-grow justify-start w-full max-h-[260px]">
            <BingoCards
              maxNumbers={maxNumberRange}
              isMobile={isMobile}
              calledNumbers={calledNumbers}
              drawDetails={drawDetails}
            />
          </div>
          <div
            className={`flex-shrink-0`}
          >
            <UserProfile
              userWalletAmount={unerAmount}
              remainingPatterns={remainingPatterns}
              userWallet={userWallet}
              isMobile={isMobile}
              drawDetails={drawDetails}
              autoClaim={autoClaim}
              toggleAutoClaim={toggleAutoClaim}
            />
          </div>
        </div>
        <div className="flex  xl:hidden  justify-start w-full max-h-[260px] mt-4">
          <BingoCards
            maxNumbers={maxNumberRange}
            isMobile={isMobile}
            calledNumbers={calledNumbers}
            drawDetails={drawDetails}
          />
        </div>

        <div className="mt-4">
          <DemoBingoTickets
            handleAmount={handleAmount}
            amount={amount}
            handleClaimAnimation={handleClaimAnimation}
            isMobile={isMobile}
            calledNumbers={calledNumbers}
            autoHighlightPattern={autoClaim}
            remainingPatterns={remainingPatterns}
            ticketData={ticketData}
            drawDetails={drawDetails}
            handleRemainingCount={handleRemainingCount}
            handleWinnersData={handleWinnersData}
            winningTickets={winningTickets}
          />
        </div>

        {showBingoAnimation && (
          <div className="absolute z-50  justify-center items-center  flex w-full">
            <img
              src="/assets/Bingo.gif"
              alt="Bingo!"
              className="w-full h-full object-contain"
            />
          </div>
        )}
      </div>
    </>
  );
}
